
<template>
  <div
    class="v-date-picker"
    :data-cy="`${dataCy}--VDateTimePicker-bloc`"
  >
    <ValidationProvider
      :ref="validationProviderRef"
      :mode="veeValidateInteractionMode"
      :rules="rules"
      :vid="validationProviderRef"
      :name="label"
    >
      <v-dialog
        ref="menu"
        v-model="isMenuOpen"
        :closeOnContentClick="false"
        persistent
        maxWidth="350"
        transition="fade-transition"
      >
        <template v-slot:activator="{ }">
          <v-text-field
            :id="validationProviderRef"
            v-model="dateTime"
            :label="label"
            :color="color"
            :disabled="disabled"
            prependInnerIcon="event_available"
            :outlined="!!isOutlined"
            :data-cy="`${dataCy}--textinput`"
            :dense="dense"
            v-bind="$attrs"
            clearable
            @click:clear="onClear"
            @click="onTextFieldClick"
            @click:prepend-inner="onIconClick"
            @blur="onTextFieldBlur"
            @keyup="onEnterPress"
          />
        </template>

        <v-card
          class="w-100"
          style="min-height: 300px;"
        >
          <v-card-title>
            <v-row class="p-0">
              <v-col
                cols="6"
                class="p-0"
              >
                <v-btn
                  tile
                  minWidth="100%"
                  color="primary"
                  :elevation="0"
                  outlined
                  @click="isCalendar = true"
                >
                  <v-icon>
                    mdi-calendar
                  </v-icon>
                </v-btn>
              </v-col>

              <v-col
                cols="6"
                class="p-0"
              >
                <v-btn
                  tile
                  minWidth="100%"
                  color="primary"
                  :elevation="0"
                  @click="isCalendar = false"
                >
                  <v-icon>
                    mdi-clock
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-subtitle class="text-center p-3 h-5">
            {{ dateTimeSelected }}
          </v-card-subtitle>
          <v-card-text
            class="p-2 text-center"
          >
            <v-row
              class="p-0"
            >
              <v-col
                class="p-0"
              >
                <v-date-picker
                  v-if="isCalendar"
                  v-model="date"
                  :color="color"
                  :disabled="disabled"
                  :locale="datePickerLocales[userLang]"
                  :data-cy="`${dataCy}--datepicker`"
                  noTitle
                  scrollable
                  style="height:320px;"
                  @input="isCalendar = false"
                />
                <vue-timepicker
                  v-else
                  v-model="time"
                  :format="userLang === 'fr' ? 'HH:mm' : 'hh:mm A'"
                  :disabled="disabled"
                  manualInput
                  style="padding-top: 10px; padding-bottom: 20%;"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-spacer />
          <v-card-actions class="justify-end m-b-0">
            <v-btn
              text
              color="primary"
              @click="onClickCancel"
            >
              {{ $t('forms.btn.cancel') }}
            </v-btn>
            <v-btn
              text
              :disabled="!date || isEmpty(time)"
              :data-cy="cyData.workOrders.workOrderView.buttons.confirmTime"
              color="primary"
              @click="onClickSave"
            >
              {{ $t('forms.btn.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </ValidationProvider>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

import { ValidationProvider } from 'vee-validate';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import _datePickerLocales from '@/locales/date-picker-locales';
import { workOrders } from '@/aq-data-cy.json';
import VueTimepicker from 'vue2-timepicker';
import { isEmpty } from 'lodash';

export default {
    components: {
        ValidationProvider,
        VueTimepicker,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        value: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: null,
        },
        rules: {
            type: Object,
            default: () => ({ required: false }),
        },
        color: {
            type: String,
            default: '#003876',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        format: {
            type: String,
            default: 'L LT',
            validation: value => ['24hr', 'L LT'].indexOf(value) !== -1,
        },
        // with fr it's 'fr-FR', with en it's 'en-US'
        locale: {
            type: String,
            default: 'en-US',
        },
        viewonly: {
            type: Boolean,
            default: false,
        },
        isOutlined: {
            type: Boolean,
            default: true,
        },
        dataCy: {
            type: String,
            default: '',
        },
        dense: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            date: null,
            time: {},
            isCalendar: true,
            isMenuOpen: false,
            tempDateTime: null,

        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamDateFormat',
            'lang',
        ]),
        dateTimeSelected() {
            const date = this.date || '';
            let time = '';
            if (!isEmpty(this.time)) {
                time = this.userLang === 'fr' ? `${this?.time?.HH}:${this?.time?.mm}` : `${this?.time?.hh}:${this?.time?.mm} ${this?.time?.A}`;
            }

            return `${date} ${time}`;
        },
        dateTime: {
            get() {
                return this.value ? this.$options.filters.formatDate(this.value, this.format) : null;
            },
            set(newValue) {
                this.tempDateTime = newValue;
            },
        },
        datePickerLocales() {
            return _datePickerLocales;
        },
        cyData() {
            return {
                workOrders,
            };
        },
        containerClass() {
            return this.dense ? 'v-date-picker--inputHeight' : 'v-date-picker';
        },
    },
    watch: {
        value: {
            handler(newValue) {
                if (!newValue) {
                    return;
                }
                const formatToApply = this.teamDateFormat || this.lang;
                const localeFormat = this.datePickerLocales[formatToApply] ? this.datePickerLocales[formatToApply].format : this.format;
                const momentValue = moment(newValue, localeFormat);
                if (momentValue.isValid()) {
                    this.date = momentValue.format('YYYY-MM-DD');
                    this.time = this.userLang === 'en' ? this.convertTimeTo12Hours(momentValue) : this.timeIn24Format(momentValue);
                }
            },
            immediate: true,
        },
        date: {
            handler(newValue) {
                this.dateTime = newValue;
            },
        },
    },
    methods: {
        isEmpty(value) {
            return isEmpty(value);
        },
        onClickCancel() {
            this.reset();
        },
        onClickSave() {
            const time = this.userLang === 'en' ? this.convertTo24Hours(this.time) : `${this.time.HH}:${this.time.mm}`;
            this.$refs.menu.save();
            this.$emit('input', `${this.date}T${time}`);
            this.reset();
        },
        reset() {
            this.isMenuOpen = false;
            this.date = null;
            this.time = null;
            this.isCalendar = true;
        },
        setDateTimeNow() {
            const teamDateFormat = this.$store.getters.teamDateFormat || this.$store.getters.lang;
            const localeFormat = this.datePickerLocales[teamDateFormat] ? this.datePickerLocales[teamDateFormat].format : this.format;
            const now = moment().format(localeFormat);
            this.$emit('input', now);
            this.dateTime = now;
        },
        dateFormat() {
            const dateTimeToSave = this.tempDateTime ? this.tempDateTime : this.dateTime;
            const parsedMoment = moment(dateTimeToSave, 'DD/MM/YYYY HH:mm', true);
            if (parsedMoment.isValid()) {
                this.$emit('input', parsedMoment.format());
            }
        },
        onTextFieldBlur() {
            this.dateFormat();
        },
        onTextFieldClick() {
            if (this.dateTime === '' || this.dateTime === null) {
                this.setDateTimeNow();
            }
        },
        onIconClick() {
            this.isMenuOpen = true;
        },
        onClear() {
            this.dateTime = null;
            this.tempDateTime = null;
            this.date = null;
            this.time = {};
            this.$emit('input', null);
        },
        onEnterPress(event) {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                this.dateFormat();
                event.target.blur();
            }
        },
        convertTimeTo12Hours(time) {
            const [hh, mm] = time.format('HH:mm').split(':');
            const period = hh >= 12 ? 'PM' : 'AM';
            let hour = hh % 12 || 12;
            if (period === 'AM' && hh !== 12) {
                hour = `0${hour}`;
            }
            return {
                hh: hour,
                mm,
                A: period,
            };
        },
        convertTo24Hours(time) {
            const { hh, mm, A: period } = time;
            let hours = parseInt(hh, 10);

            if (period === 'PM' && hours !== 12) {
                hours += 12;
            } else if (period === 'AM' && hours === 12) {
                hours = 0;
            }

            // Add leading zero if necessary
            hours = hours.toString().padStart(2, '0');
            const min = mm.padStart(2, '0');

            return `${hours}:${min}`;
        },
        timeIn24Format(time) {
            const [HH, mm] = time.format('HH:mm').split(':');
            return { HH, mm };
        },
    },
};

</script>

<style lang="scss" scoped>
  .v-date-picker {
    height: 53px !important;
  }
  .v-date-picker--inputHeight {
    height: auto !important;
  }
</style>
